import { FieldDef, GroupDef, GuiDef, ValidatorDef, TabDefinition, CollapsableDef } from '@/objects/gui';
import { BibAssignSort } from '@/objects/generated';
import { markRaw, computed } from 'vue';
import { InputSelect, DataSourceLookup } from '@/components';
import { useBibAssignStore, useCommonStore } from '@/stores';
import { storeToRefs } from 'pinia';
import type { SelectLine, SelectLineValueType } from '@/objects/SelectLine';

export class BibAssignSortGuiDef extends GuiDef<BibAssignSort> {
    constructor() {
        super('BibAssignSort');

        const BibAssign = storeToRefs(useBibAssignStore());
        const BibAssignSortTypeSource = computed(() => {
            return BibAssign.BibAssignSortTypeAsSelectLine.value;
        });
        const Common = storeToRefs(useCommonStore());
        const SortDirectionSource = computed(() => {
            return Common.SortDirectionAsSelectLine.value;
        });
        const BibAssignPlaceUnseededSource = computed(() => {
            return BibAssign.BibAssignPlaceUnseededAsSelectLine.value;
        });

        const SortType = new FieldDef<BibAssignSort>();
        SortType.Name = 'SortType';
        SortType.FieldName = 'SortType';
        SortType.DefinedFor = 'Both';
        SortType.TranslationKey = 'bibassignsort.sorttype';
        SortType.Editable = true;
        SortType.Filterable = false;
        SortType.Sortable = false;
        SortType.CssClasses = '';
        SortType.value = (bibassignsort) => bibassignsort.SortType;
        SortType.setValue = (bibassignsort, value) => (bibassignsort.SortType = value);
        SortType.DetailEditor = markRaw(InputSelect);
        SortType.DetailEditorProps = { virtualScrollerMaxItems: 30, datasource: { mapsource: BibAssignSortTypeSource } };
        SortType.CellEditor = markRaw(InputSelect);
        SortType.CellEditorProps = { virtualScrollerMaxItems: 30, datasource: { mapsource: BibAssignSortTypeSource }, autoOpen: true };
        // @ts-expect-error @ts-ignore
        SortType.CellRenderer = markRaw(DataSourceLookup);
        SortType.CellRendererProps = { datasource: { mapsource: BibAssignSortTypeSource } };
        SortType.ExportValueFormatter = (obj: BibAssignSort) => {
            return BibAssignSortTypeSource.value.get(obj.SortType)?.text;
        };
        SortType.Validator = null;
        SortType.SortDirection = undefined;
        SortType.cellClassRules = undefined;
        SortType.MinWidth = undefined;
        SortType.MaxWidth = undefined;
        SortType.Tooltip = null;
        this.Fields.push(SortType);

        const Direction = new FieldDef<BibAssignSort>();
        Direction.Name = 'Direction';
        Direction.FieldName = 'Direction';
        Direction.DefinedFor = 'Both';
        Direction.TranslationKey = 'bibassignsort.direction';
        Direction.Editable = true;
        Direction.Filterable = false;
        Direction.Sortable = false;
        Direction.CssClasses = '';
        Direction.value = (bibassignsort) => bibassignsort.Direction;
        Direction.setValue = (bibassignsort, value) => (bibassignsort.Direction = value);
        Direction.DetailEditor = markRaw(InputSelect);
        Direction.DetailEditorProps = { virtualScrollerMaxItems: 30, datasource: { mapsource: SortDirectionSource } };
        Direction.CellEditor = markRaw(InputSelect);
        Direction.CellEditorProps = { virtualScrollerMaxItems: 30, datasource: { mapsource: SortDirectionSource }, autoOpen: true };
        // @ts-expect-error @ts-ignore
        Direction.CellRenderer = markRaw(DataSourceLookup);
        Direction.CellRendererProps = { datasource: { mapsource: SortDirectionSource } };
        Direction.ExportValueFormatter = (obj: BibAssignSort) => {
            return SortDirectionSource.value.get(obj.Direction)?.text;
        };
        Direction.Validator = null;
        Direction.SortDirection = undefined;
        Direction.cellClassRules = undefined;
        Direction.MinWidth = undefined;
        Direction.MaxWidth = undefined;
        Direction.Tooltip = null;
        this.Fields.push(Direction);

        const PlaceUnseededLast = new FieldDef<BibAssignSort>();
        PlaceUnseededLast.Name = 'PlaceUnseededLast';
        PlaceUnseededLast.FieldName = 'PlaceUnseededLast';
        PlaceUnseededLast.DefinedFor = 'Both';
        PlaceUnseededLast.TranslationKey = 'bibassignsort.placeunseededlast';
        PlaceUnseededLast.Editable = true;
        PlaceUnseededLast.Filterable = false;
        PlaceUnseededLast.Sortable = false;
        PlaceUnseededLast.CssClasses = '';
        PlaceUnseededLast.value = (bibassignsort) => bibassignsort.PlaceUnseededLast;
        PlaceUnseededLast.setValue = (bibassignsort, value) => (bibassignsort.PlaceUnseededLast = value);
        PlaceUnseededLast.DetailEditor = markRaw(InputSelect);
        PlaceUnseededLast.DetailEditorProps = { virtualScrollerMaxItems: 30, datasource: { mapsource: BibAssignPlaceUnseededSource } };
        PlaceUnseededLast.CellEditor = markRaw(InputSelect);
        PlaceUnseededLast.CellEditorProps = {
            virtualScrollerMaxItems: 30,
            datasource: { mapsource: BibAssignPlaceUnseededSource },
            autoOpen: true,
        };
        // @ts-expect-error @ts-ignore
        PlaceUnseededLast.CellRenderer = markRaw(DataSourceLookup);
        PlaceUnseededLast.CellRendererProps = { datasource: { mapsource: BibAssignPlaceUnseededSource } };
        PlaceUnseededLast.ExportValueFormatter = (obj: BibAssignSort) => {
            return BibAssignPlaceUnseededSource.value.get(obj.PlaceUnseededLast)?.text;
        };
        PlaceUnseededLast.Validator = null;
        PlaceUnseededLast.SortDirection = undefined;
        PlaceUnseededLast.cellClassRules = undefined;
        PlaceUnseededLast.MinWidth = undefined;
        PlaceUnseededLast.MaxWidth = undefined;
        PlaceUnseededLast.Tooltip = null;
        this.Fields.push(PlaceUnseededLast);
    }

    public async LoadDependencies() {
        await useBibAssignStore().EnsureLoaded(['BibAssignSortType']);
        await useCommonStore().EnsureLoaded(['SortDirection']);
        await useBibAssignStore().EnsureLoaded(['BibAssignPlaceUnseeded']);
    }
}
